import { Modal } from './accessibility.jsx';
import { confirmAction } from './feedback.jsx';
import { errorText } from './workbench-logic.mjs';

const COPY = {
  en: {
    title: 'Offline permissions', home: 'Home company', role: 'Team Admin (offline)', access: 'Offline access',
    stored: 'Stored Team Admin role', membership: 'Explicit offline membership', source: 'Access source',
    eligible: 'Eligible developer', effective: 'Effective management', yes: 'Enabled', no: 'Disabled', unknown: 'Unknown',
    sources: { home: 'Home workspace', membership: 'Explicit membership', none: 'No offline access', unavailable: 'Unavailable' },
    workflow: 'Management requires an active ordinary developer, offline access and the Team Admin role. Normally, grant offline access first, then grant the role. Each action is saved separately.',
    warning: 'Each change signs this user out and invalidates existing connections. They must sign in and reconnect.',
    homeNote: 'Home access does not depend on an explicit membership. Removing an additional membership does not revoke home access.',
    grantAccess: 'Grant offline access', revokeAccess: 'Revoke offline access', removeExtra: 'Remove additional membership',
    grantRole: 'Grant Team Admin (offline)', revokeRole: 'Revoke Team Admin (offline)',
    close: 'Close', retry: 'Retry', loading: 'Reloading current state…', pending: 'Saving and reloading current state…',
    unknownState: 'Current state unknown. Reload before making another change.',
    reloadFailed: 'Current state could not be reloaded. Retry only reloads; it does not repeat a change.',
    saved: label => `${label}: saved.`,
    reasons: {
      'No offline workspace is configured': 'No offline workspace is configured',
      'Offline workspace configuration is ambiguous': 'Offline workspace configuration is ambiguous',
      'Offline workspace is inactive': 'Offline workspace is inactive',
      'Requires an active ordinary developer': 'Requires an active ordinary developer',
      'Offline access is required': 'Offline access is required',
      'Team Admin role is not enabled': 'Team Admin role is not enabled',
    },
  },
  he: {
    title: 'הרשאות אופליין', home: 'חברת הבית', role: 'מנהל צוות (אופליין)', access: 'גישה לאופליין',
    stored: 'תפקיד מנהל צוות שמור', membership: 'חברות מפורשת באופליין', source: 'מקור הגישה',
    eligible: 'כשיר כמפתח', effective: 'ניהול בפועל', yes: 'מופעל', no: 'כבוי', unknown: 'לא ידוע',
    sources: { home: 'מרחב הבית', membership: 'חברות מפורשת', none: 'אין גישה לאופליין', unavailable: 'לא זמין' },
    workflow: 'ניהול דורש מפתח רגיל פעיל, גישה לאופליין ותפקיד מנהל צוות. בדרך כלל מעניקים קודם גישה לאופליין ואז את התפקיד. כל פעולה נשמרת בנפרד.',
    warning: 'כל שינוי מנתק את המשתמש ומבטל חיבורים קיימים. עליו להיכנס מחדש ולחבר מחדש את החיבורים.',
    homeNote: 'גישת הבית אינה תלויה בחברות מפורשת. הסרת חברות נוספת אינה מבטלת את גישת הבית.',
    grantAccess: 'הענקת גישה לאופליין', revokeAccess: 'ביטול גישה לאופליין', removeExtra: 'הסרת חברות נוספת',
    grantRole: 'הענקת תפקיד מנהל צוות (אופליין)', revokeRole: 'ביטול תפקיד מנהל צוות (אופליין)',
    close: 'סגירה', retry: 'ניסיון חוזר', loading: 'טעינת המצב הנוכחי…', pending: 'שמירה וטעינת המצב הנוכחי…',
    unknownState: 'המצב הנוכחי אינו ידוע. יש לטעון מחדש לפני שינוי נוסף.',
    reloadFailed: 'לא ניתן לטעון את המצב הנוכחי. ניסיון חוזר רק טוען מחדש ואינו חוזר על השינוי.',
    saved: label => `${label}: נשמר.`,
    reasons: {
      'No offline workspace is configured': 'לא הוגדר מרחב אופליין',
      'Offline workspace configuration is ambiguous': 'הגדרת מרחב האופליין אינה חד־משמעית',
      'Offline workspace is inactive': 'מרחב האופליין אינו פעיל',
      'Requires an active ordinary developer': 'נדרש מפתח רגיל פעיל',
      'Offline access is required': 'נדרשת גישה לאופליין',
      'Team Admin role is not enabled': 'תפקיד מנהל צוות אינו מופעל',
    },
  },
};

function refusalText(error) {
  return error?.detail && typeof error.detail === 'object' ? JSON.stringify(error.detail) : errorText(error);
}

export function OfflineAuthorityDialog({ lang, user, tenant, reloadUser, onClose }) {
  const t = COPY[lang === 'he' ? 'he' : 'en'];
  const descriptionId = React.useId();
  const heading = React.useRef(null);
  const live = React.useRef(false);
  const pending = React.useRef(true);
  const [state, setState] = React.useState({ row: user, unknown: false, busy: 'read', error: '', notice: '' });

  const reconcile = async (notice, error = '') => {
    try {
      const fresh = await reloadUser();
      if (!fresh.offline_authority) throw new Error(t.unknownState);
      if (live.current) setState({ row: fresh, unknown: false, busy: '', error, notice });
    } catch (readError) {
      if (live.current) setState(s => ({ ...s, unknown: true, busy: '', notice,
        error: [error, t.reloadFailed, refusalText(readError)].filter(Boolean).join(' ') }));
    } finally {
      pending.current = false;
    }
  };
  React.useEffect(() => {
    live.current = true;
    reconcile('');
    return () => { live.current = false; };
  }, []);

  const change = async (kind, enabled, label) => {
    if (pending.current || state.unknown) return;
    pending.current = true;
    setState(s => ({ ...s, busy: 'confirm', error: '' }));
    const approved = await confirmAction(`${label}? ${t.warning}`, { lang, danger: !enabled, confirmLabel: label });
    if (!live.current) return;
    heading.current?.focus();
    if (!approved) {
      pending.current = false;
      setState(s => ({ ...s, busy: '' }));
      return;
    }
    setState(s => ({ ...s, busy: 'write' }));
    let notice = state.notice, error = '';
    try {
      await (kind === 'access' ? PCMAdapter.setUserOfflineAccess : PCMAdapter.setUserTeamAdmin)(user.id, enabled);
      notice = t.saved(label);
    } catch (writeError) {
      error = `${label}: ${refusalText(writeError)}`;
    }
    if (live.current) await reconcile(notice, error);
  };
  const retry = () => {
    if (pending.current) return;
    pending.current = true;
    setState(s => ({ ...s, busy: 'read', error: '' }));
    reconcile(state.notice);
  };
  const authority = state.row.offline_authority;
  const unavailable = state.unknown || !!state.busy;
  const canEnableRole = authority.eligible === true;
  const canEnableAccess = canEnableRole && authority.access_source !== 'unavailable' && authority.offline_membership !== null;
  const home = authority.access_source === 'home' || (tenant?.offline && authority.offline_membership !== null);
  const accessLabel = authority.offline_membership ? (home ? t.removeExtra : t.revokeAccess) : t.grantAccess;
  const roleLabel = authority.team_admin ? t.revokeRole : t.grantRole;
  const value = flag => state.unknown || flag === null ? t.unknown : flag ? t.yes : t.no;
  const close = () => { if (!pending.current) onClose(); };

  return <Modal label={t.title} descriptionId={descriptionId} dir={lang === 'he' ? 'rtl' : 'ltr'}
                onClose={close}>
    <h2 ref={heading} tabIndex={-1}>{t.title}</h2>
    <p dir="auto"><strong>{user.display_name || user.username}</strong> {user.display_name && <span className="mono" dir="ltr">{user.username}</span>}</p>
    <p className="set-note" dir="auto">{t.home}: {tenant?.company_name || t.unknown}</p>
    <p className="set-note" id={descriptionId}>{t.workflow}</p>
    <p className="set-note">{t.warning}</p>
    <dl>
      {[[t.stored, value(authority.team_admin)], [t.membership, value(authority.offline_membership)],
        [t.source, state.unknown ? t.unknown : t.sources[authority.access_source]],
        [t.eligible, value(authority.eligible)], [t.effective, value(authority.management_effective)]].map(([label, text]) =>
        <div className="set-row" key={label}><dt>{label}</dt><dd>{text}</dd></div>)}
    </dl>
    {!state.unknown && authority.reason && <p className="set-note" dir="auto">{t.reasons[authority.reason] || authority.reason}</p>}
    {home && <p className="set-note">{t.homeNote}</p>}
    {state.unknown && <p className="set-note">{t.unknownState}</p>}
    <div className="set-row">
      {(!home || authority.offline_membership) && <button type="button" className="btn btn-sm" disabled={unavailable || (!authority.offline_membership && !canEnableAccess)}
        onClick={() => change('access', !authority.offline_membership, accessLabel)}>{accessLabel}</button>}
      <button type="button" className="btn btn-sm" disabled={unavailable || (!authority.team_admin && !canEnableRole)}
        onClick={() => change('role', !authority.team_admin, roleLabel)}>{roleLabel}</button>
    </div>
    <p role="status" className="set-status" dir="auto">{[state.notice, state.busy === 'read' ? t.loading : state.busy === 'write' ? t.pending : ''].filter(Boolean).join(' ')}</p>
    {state.error && <p role="alert" className="set-error" dir="auto">{state.error}</p>}
    <div className="dlg-actions">
      {state.unknown && <button type="button" className="btn" disabled={!!state.busy} onClick={retry}>{t.retry}</button>}
      <button type="button" className="btn" disabled={!!state.busy} onClick={close}>{t.close}</button>
    </div>
  </Modal>;
}
